import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Loader2, Clock } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { LegalLayout } from '../components/LegalLayout';
import { toast } from 'sonner';

const SUPPORT_EMAIL = 'support@openmeta.fr';

export default function ContactPage() {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const isValid = name.trim() && email.includes('@') && message.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || sending) return;
    setSending(true);
    try {
      await api.post('/contact', {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      toast.success(t('contact_page.success'));
      // Open the user's email client pre-filled towards support@openmeta.fr
      const mailSubject = encodeURIComponent(subject.trim() || `Contact ARIA — ${name.trim()}`);
      const mailBody = encodeURIComponent(
        `${message.trim()}\n\n—\n${name.trim()}\n${email.trim()}`
      );
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${mailSubject}&body=${mailBody}`;
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      console.error('Contact submit error:', err);
      toast.error(t('contact_page.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout>
      <div data-testid="contact-page">
        <div className="flex items-center gap-3 mb-2">
          <Mail size={26} className="text-cyan-400" />
          <h1 className="text-2xl font-bold text-foreground">{t('contact_page.title')}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">{t('contact_page.desc')}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card className="bg-[hsl(210,18%,9%)] border-border md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('contact_page.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('contact_page.name')} *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('contact_page.name_placeholder')}
                      className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('contact_page.email')} *</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('contact_page.email_placeholder')}
                      className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t('contact_page.subject')}</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={t('contact_page.subject_placeholder')}
                    className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t('contact_page.message')} *</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('contact_page.message_placeholder')}
                    rows={6}
                    className="text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)] resize-y"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={!isValid || sending}
                  className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                  data-testid="contact-submit-button"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {sending ? t('contact_page.sending') : t('contact_page.send')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-[hsl(210,18%,9%)] border-border h-fit">
            <CardContent className="py-5 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">{t('contact_page.direct')}</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors duration-150"
                  data-testid="contact-direct-email"
                >
                  <Mail size={15} />
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Clock size={14} className="shrink-0 mt-0.5 text-teal-400" />
                {t('contact_page.response_time')}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </LegalLayout>
  );
}
